import { Controller, FieldError, useFormContext } from 'react-hook-form';
// Types
import { GetPlaceDetails_getPlaceDetails } from 'api/places/types/GetPlaceDetails';
import { AccountSubType } from 'api/graphql-global-types';
// Constants
import { USER_SUBTYPES, USER_SUBTYPES_FOR_INVITE_LINK } from 'constants/user';
import { ATHLETIC_LEVEL, GENDER_OPTIONS } from 'constants/store';
// Helpers
import { joinLocation } from 'helpers/place';
import { isAthleteOrContentCreator } from 'helpers/accountType';
import { getSignUpInviteCodeFromLocalStorage } from 'helpers/storage';
// Context
import { OnboardingFormData } from 'context/OnboardingContext';
// Hooks
import { useGetSports } from 'hooks';
// Ui
import SelectWithMobileSheet from 'ui3/MobileSelectSheet/SelectWithMobileSheet';
import TextArea from 'ui3/TextArea/TextArea';
import PhoneNumberInput from 'ui3/PhoneInput/PhoneNumberInput';
import PlaceAutocomplete from 'ui3/PlaceAutocomplete/PlaceAutocomplete';
import CheckBox from 'ui3/CheckBox/CheckBox';
import DatePicker from 'ui3/DatePicker/DatePicker';
import Input from 'ui3/Input/Input';
// Components
import OTPModal from 'components/SignUp/components/OTPModal/OTPModal';
// Styles
import styles from './BasicInfoForm.module.scss';

const BasicInfoForm = () => {
  const { data } = useGetSports();
  const sports = data?.sports || [];

  const {
    errors,
    register,
    watch,
    control,
    getValues,
  } = useFormContext<OnboardingFormData>();

  const accountType = watch('accountType');
  const showBirthDateAndGender = isAthleteOrContentCreator(accountType);

  const isInviteLink = getSignUpInviteCodeFromLocalStorage();
  const subtypes = isInviteLink ? USER_SUBTYPES_FOR_INVITE_LINK : USER_SUBTYPES;

  return (
    <>
      <div className={styles.root}>
        <Controller
          name="accountType"
          control={control}
          render={({ onChange, value }) => (
            <SelectWithMobileSheet
              name="accountType"
              label="Category"
              placeholder="Select"
              onChange={onChange}
              value={value}
              options={subtypes}
              error={(errors.accountType as FieldError)?.message}
            />
          )}
        />

        {accountType?.value === AccountSubType.Athlete && (
          <Controller
            name="experienceLevel"
            control={control}
            render={({ onChange, value }) => (
              <SelectWithMobileSheet
                name="experienceLevel"
                label="Experience Level"
                placeholder="Select"
                onChange={onChange}
                value={value}
                options={ATHLETIC_LEVEL}
                error={errors.experienceLevel?.value?.message}
              />
            )}
          />
        )}

        {showBirthDateAndGender && (
          <>
            <Controller
              name="birthDate"
              control={control}
              render={({ onChange, value }) => (
                <DatePicker
                  showYearDropdown
                  showMonthDropdown
                  yearDropdownItemNumber={70}
                  maxDate={new Date()}
                  openToDate={new Date('2005/01/01')}
                  dateFormat="MM/dd/yyyy"
                  selected={value}
                  onChange={onChange}
                  showTimeSelect={false}
                  placeholderText="mm/dd/yyyy"
                  customInput={
                    <Input
                      label="Birthday"
                      name="birthDate"
                      aria-label="Birthday"
                      error={errors.birthDate?.message}
                      endIcons={[{ name: 'calendarSelect' }]}
                    />
                  }
                />
              )}
            />

            <Controller
              name="gender"
              control={control}
              render={({ onChange, value }) => (
                <SelectWithMobileSheet
                  name="gender"
                  label="Gender"
                  placeholder="Select"
                  onChange={onChange}
                  value={value}
                  options={GENDER_OPTIONS}
                  error={
                    (errors.gender as FieldError)?.message ||
                    (errors.gender as { value?: FieldError })?.value?.message
                  }
                />
              )}
            />
          </>
        )}

        <Controller
          name="sport"
          control={control}
          render={({ onChange, value }) => (
            <SelectWithMobileSheet
              name="sport"
              label="Sport"
              placeholder="Select"
              onChange={onChange}
              value={value}
              options={sports}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
              error={(errors.sport as FieldError)?.message}
            />
          )}
        />

        <TextArea
          name="bio"
          ref={register}
          label="Bio (optional)"
          placeholder="Tell us more about yourself"
          error={errors.bio?.message}
          maxLength={300}
        />

        <Controller
          name="location"
          control={control}
          render={({ onChange, value }) => {
            const handlePlaceSelect = (
              placeDetails: GetPlaceDetails_getPlaceDetails
            ) => {
              const location = {
                country: placeDetails.country,
                countryCode: placeDetails.countryCode,
                city: placeDetails.city,
                state: placeDetails.stateCode,
                stateCode: placeDetails.stateCode,
                coordinates: {
                  latitude: placeDetails.location.latitude,
                  longitude: placeDetails.location.longitude,
                },
              };

              onChange(location);
            };

            return (
              <PlaceAutocomplete
                name="location"
                label="Location"
                placeholder="Enter location"
                mobileSheet
                onChange={handlePlaceSelect}
                value={value ? joinLocation([value.city, value.country]) : ''}
                error={(errors.location as FieldError)?.message}
              />
            );
          }}
        />

        <Controller
          name="phoneNumber"
          control={control}
          render={({ onChange, value }) => (
            <PhoneNumberInput
              name="phoneNumber"
              label="Your Phone Number"
              placeholder="Phone Number"
              onChange={onChange}
              value={value}
              error={errors.phoneNumber?.message}
            />
          )}
        />

        <CheckBox
          alignLabelTop
          label="By checking this box, you agree to receive SMS text messages from MILLIONS regarding support and updates for your profile. Reply STOP to opt out at any time. Reply HELP for Customer Care Contact Information. Messages and data rates may apply. Message frequency will vary."
          name="phoneOptIn"
          ref={register}
          error={errors.phoneOptIn?.message}
        />
      </div>
      <OTPModal
        phoneValue={getValues('phoneNumber') || ''}
        emailValue={getValues('email') || ''}
      />
    </>
  );
};

export default BasicInfoForm;
